import { Injectable } from '@angular/core';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { delay, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../../core';
import { of } from 'rxjs';

interface SnackbarData {
  message: string;
  config: MatSnackBarConfig;
  action: string;
  isDispatched: boolean;
}

@Injectable({ providedIn: 'root' })
export class AlertService extends BaseComponent {
  private snackBarQueue: SnackbarData[] = [];
  private PANEL_CLASS_ERROR = 'alertError';

  private setAutoHide = true;
  private autoHide = 15000;
  private horizontalPosition: MatSnackBarHorizontalPosition = 'center';
  private verticalPosition: MatSnackBarVerticalPosition = 'top';
  private maxChar = 350;

  constructor(private snackBar: MatSnackBar) {
    super();
    this.executeSnackbarQueue();
  }

  public alertErrorMessage(message: string): void {
    this.alertMessage(message, this.PANEL_CLASS_ERROR);
  }

  private alertMessage(message: string, panelClass: string): void {
    if (!message) {
      return;
    }
    const messageToAlert = this.formatMessage(message);

    if (this.isMessageDuplicated(messageToAlert, panelClass)) {
      return;
    }

    const config = new MatSnackBarConfig();
    config.verticalPosition = this.verticalPosition;
    config.horizontalPosition = this.horizontalPosition;
    config.duration = this.setAutoHide ? this.autoHide : 0;
    config.panelClass = panelClass;

    this.pushToSnackbarQueue({
      message: messageToAlert,
      config,
      action: '╳',
      isDispatched: false,
    });
  }

  private isMessageDuplicated(message: string, panelClass: string): boolean {
    return !!this.snackBarQueue?.filter(
      (snack) =>
        snack?.message === message && snack?.config?.panelClass === panelClass
    )?.length;
  }

  private pushToSnackbarQueue(snackbarData: SnackbarData) {
    this.snackBarQueue.push(snackbarData);
    this.executeSnackbarQueue();
  }

  private popFromSnackbarQueue() {
    if (this.snackBarQueue?.length && this.snackBarQueue[0]?.isDispatched) {
      this.snackBarQueue = this.snackBarQueue.filter(
        (snackBar) => !snackBar.isDispatched
      );
      this.executeSnackbarQueue();
    }
  }

  private executeSnackbarQueue() {
    if (this.snackBarQueue?.length) {
      const firstSnackbar = this.snackBarQueue[0];
      if (!firstSnackbar.isDispatched) {
        firstSnackbar.isDispatched = true;

        const snackBarRef = this.snackBar.open(
          firstSnackbar.message,
          firstSnackbar.action,
          firstSnackbar.config
        );

        snackBarRef
          .afterDismissed()
          .pipe(takeUntil(this.componentDestroyed))
          .subscribe(() => {
            this.popFromSnackbarQueue();
          });

        of('')
          .pipe(takeUntil(this.componentDestroyed), delay(this.autoHide))
          .subscribe(() => {
            try {
              snackBarRef.dismiss();
            } catch (e) {}
          });
      }
    }
  }

  private formatMessage(message: string): string {
    if (message?.length <= this.maxChar) {
      return message;
    }

    return message.substring(0, this.maxChar) + '...';
  }
}
